/**
 * 离线邮箱轮询器（ARCH T03.5）。
 *
 * 三条触发路径：
 *  1. 上线首次拉取（防「推送竞态」：连接建立前中继已 flush 过一次）
 *  2. 定时 30s 兜底（推送丢包 / 重连后补漏）
 *  3. 页面重新可见（手机切后台回来立刻拉）
 *
 * 拉到后由 `Libp2pBrowserPeer` 逐条派发并回 ACK，服务端据此删除条目。
 */

import { MAILBOX_POLL_INTERVAL_MS } from '@/config';
import { createLogger } from '@/lib/logger';

const logger = createLogger('net:mailbox');

export interface MailboxPollerOptions {
  /** 执行一次拉取，返回本次取到的信封条数。 */
  poll: () => Promise<number>;
  /** 拉取结果回调（供 store 提示「补收到 N 条」）。 */
  onResult?: (count: number) => void;
  /** 轮询间隔（ms），默认 30s。 */
  intervalMs?: number;
}

export class MailboxPoller {
  private timer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private readonly intervalMs: number;
  private onVisibility: (() => void) | null = null;

  constructor(private readonly opts: MailboxPollerOptions) {
    this.intervalMs = opts.intervalMs ?? MAILBOX_POLL_INTERVAL_MS;
  }

  get isRunning(): boolean {
    return this.running;
  }

  /** 启动定时轮询 + 上线首次拉取。 */
  start(): void {
    if (this.running) return;
    this.running = true;

    void this.pollNow();

    this.timer = setInterval(() => {
      void this.pollNow();
    }, this.intervalMs);

    if (typeof document !== 'undefined') {
      this.onVisibility = () => {
        if (document.visibilityState === 'visible') void this.pollNow();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }

  /** 立即拉一次（外部也可主动调用，如「立即检查新消息」按钮）。 */
  async pollNow(): Promise<number> {
    try {
      const count = await this.opts.poll();
      if (count > 0) {
        logger.info('邮箱补收', count, '条');
        this.opts.onResult?.(count);
      }
      return count;
    } catch (err) {
      logger.warn('邮箱轮询失败', err instanceof Error ? err.message : err);
      return 0;
    }
  }

  stop(): void {
    this.running = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.onVisibility && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.onVisibility = null;
    }
  }
}

export default MailboxPoller;
